import React, { Component } from "react";
import * as s from "./AdvancedSearchOverlay.module.scss";
import { AdvancedSearchStore } from "src/stores";
import { inject, observer } from "mobx-react";
import {
  Button,
  P,
  MultiSelectDropdown,
  FilterChipCollection,
  DateRange,
  ModalToolbar
} from "src/views/shared/components";
import { Icons } from "src/libs/assets";
import { tx } from "src/libs/i18n";

interface Props {
  advancedSearchStore?: AdvancedSearchStore;
}

@inject("advancedSearchStore")
@observer
export class AdvancedSearchOverlay extends Component<Props> {
  renderSummaryRow = (title, items) => {
    const itemsLength = items && items.length;

    return (
      <div className={s.summaryRowContainer}>
        <P small className={s.summaryRowTitle}>
          {title}
        </P>

        <div className={s.summaryRowItemsContainer}>
          <img
            src={Icons.corner_arrow_small}
            alt="corner-arrow"
            className={s.summaryRowIcon}
          />
          <div className={s.summaryRowItemsWrapper}>
            {items &&
              items.map((item, key) =>
                this.renderSummaryItem(item, key, itemsLength)
              )}
          </div>
        </div>
      </div>
    );
  };

  renderSummaryItem = (item, key, itemsLength) => {
    const { value } = item;
    let addSeparator = itemsLength > 1 && key < itemsLength - 1;

    return (
      <P medium className={s.summaryRowItem} key={key}>
        {`"${value}"${addSeparator ? ", " : ""}`}
      </P>
    );
  };

  performSearchAndToggleOverlay = () => {
    const { advancedSearchStore } = this.props;
    const {
      toggleAdvancedSearchOverlay,
      performAdvancedSearch,
      setAdvancedSearchActive
    } = advancedSearchStore;

    performAdvancedSearch();
    toggleAdvancedSearchOverlay();
    setAdvancedSearchActive(true);
  };

  render() {
    const { advancedSearchStore } = this.props;
    const {
      toggleAdvancedSearchOverlay,
      toggleDropdownOption,
      advancedSearchStringTypes,
      toggleStringFilterChip,
      allStringsToInclude,
      minimumStringsToInclude,
      exactStringToInclude,
      allStringsToExclude,
      publicationYearsToInclude,
      toggleYear,
      clearAll
    } = advancedSearchStore;

    const classes = [s.advancedSearchOverlay];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const lastYear = currentDate.getFullYear() - 1;

    return (
      <div className={classes.join(" ")}>
        <div className={s.overlayTopRow}>
          <ModalToolbar
            title={tx("search.advancedSearch")}
            onClose={() => toggleAdvancedSearchOverlay()}
            button={{ title: tx("misc.clearAll"), onClick: clearAll }}
          />
        </div>
        <div className={s.overlayContentContainer}>
          <h3 className={s.overlayTitle}>
            {tx("search.narrowDownWithAdvancedSearch")}
          </h3>

          <div className={s.searchStringTypeContainer}>
            <h6 className={s.searchStringTypeTitle}>This:</h6>{" "}
            <P small className={s.searchStringTypeLabel}>
              {tx("search.titleOrAbstract")}
            </P>
            <div className={s.searchStringTypeDropdownContainer}>
              <img
                src={Icons.compass_small}
                alt="categories"
                className={s.compassIcon}
              />
              <MultiSelectDropdown
                className={s.multiSelectDropdown}
                inputPlaceholder={{
                  hasSelectedValues: tx("misc.addMore"),
                  noSelectedValues: tx("misc.anyField")
                }}
                options={advancedSearchStringTypes}
                onOptionClick={toggleDropdownOption}
              />
            </div>
          </div>
          <div className={s.stringInclusionContainer}>
            <h6 className={s.stringInclusionContainerTitle}>
              {tx("misc.contains")}:
            </h6>
            <P small className={s.stringInclusionContainerLabel}>
              {tx("search.separateWordsWithComma")}
            </P>
            <div className={s.stringInclusionInputsContainer}>
              <img
                src={Icons.file_plus_small}
                alt="categories"
                className={s.filePlusIcon}
              />
              <div className={s.filterChipCollectionsContainer}>
                <FilterChipCollection
                  inputLabel={tx("search.allOfTheseWords")}
                  inputPlaceholder={{
                    hasSelectedValues: tx("misc.addMore"),
                    noSelectedValues: tx("misc.startTyping")
                  }}
                  options={allStringsToInclude}
                  onOptionClick={toggleStringFilterChip}
                  className={s.filterChipCollection}
                  separateOnKeyCodes={[
                    { code: 188 },
                    { code: 32 },
                    { code: 13 }
                  ]}
                />
                <FilterChipCollection
                  inputLabel={tx("search.atLastOneOfTheWords")}
                  inputPlaceholder={{
                    hasSelectedValues: tx("misc.addMore"),
                    noSelectedValues: tx("misc.startTyping")
                  }}
                  options={minimumStringsToInclude}
                  onOptionClick={toggleStringFilterChip}
                  className={s.filterChipCollection}
                  separateOnKeyCodes={[
                    { code: 188 },
                    { code: 32 },
                    { code: 13 }
                  ]}
                />
                <FilterChipCollection
                  inputLabel={tx("search.thisExactPhrase")}
                  inputPlaceholder={{
                    hasSelectedValues: tx("misc.addMore"),
                    noSelectedValues: tx("misc.startTyping")
                  }}
                  options={exactStringToInclude}
                  onOptionClick={toggleStringFilterChip}
                  optionLimit={1}
                  className={s.filterChipCollection}
                  separateOnKeyCodes={[{ code: 13 }]}
                />
              </div>
            </div>
          </div>
          <div className={s.stringExclusionContainer}>
            <h6 className={s.stringExclusionContainerTitle}>
              {tx("misc.not")}:
            </h6>
            <P small className={s.stringExclusionContainerLabel}>
              {tx("search.separateWordsWithComma")}
            </P>
            <div className={s.stringExclusionInputsContainer}>
              <img
                src={Icons.file_minus_small}
                alt="categories"
                className={s.fileMinusIcon}
              />
              <div className={s.filterChipCollectionsContainer}>
                <FilterChipCollection
                  inputLabel={tx("search.noneOfTheseWords")}
                  inputPlaceholder={{
                    hasSelectedValues: tx("misc.addMore"),
                    noSelectedValues: tx("misc.startTyping")
                  }}
                  options={allStringsToExclude}
                  onOptionClick={toggleStringFilterChip}
                  className={s.filterChipCollection}
                  separateOnKeyCodes={[
                    { code: 188 },
                    { code: 32 },
                    { code: 13 }
                  ]}
                />
              </div>
            </div>
          </div>
          <div className={s.dateRangeContainer}>
            <h6 className={s.dateRangeContainerTitle}>
              {tx("search.andIsPublished")}
            </h6>
            <P small className={s.dateRangeContainerLabel}>
              {tx("search.returnArticlesDatedBetween")}
            </P>
            <div className={s.dateRangeInputsContainer}>
              <img
                src={Icons.calendar_small}
                alt="categories"
                className={s.calendarIcon}
              />
              <DateRange
                lowerRangeLabel={tx("dateRangeDropdown.lowerRangeLabel")}
                lowerRangePlaceholder="YYYY"
                lowerRangeMax={lastYear}
                upperRangeLabel={tx("dateRangeDropdown.upperRangeLabel")}
                upperRangePlaceholder="YYYY"
                upperRangeMax={currentYear}
                onInputChange={toggleYear}
              />
            </div>
          </div>
          <div className={s.searchSummaryContainer}>
            <P className={s.searchSummaryLabel}>Summary:</P>
            {allStringsToInclude &&
              allStringsToInclude.length > 0 &&
              this.renderSummaryRow(
                tx("search.anyFieldContainsAllWords"),
                allStringsToInclude
              )}
            {minimumStringsToInclude &&
              minimumStringsToInclude.length > 0 &&
              this.renderSummaryRow(
                tx("search.atLastOneOfTheWords"),
                minimumStringsToInclude
              )}
            {exactStringToInclude &&
              exactStringToInclude.length > 0 &&
              this.renderSummaryRow(
                tx("search.thisExactPhrase"),
                exactStringToInclude
              )}
            {allStringsToExclude &&
              allStringsToExclude.length > 0 &&
              this.renderSummaryRow(
                tx("search.noneOfTheseWords"),
                allStringsToExclude
              )}
            {publicationYearsToInclude &&
              publicationYearsToInclude.length > 0 &&
              this.renderSummaryRow(
                tx("search.withinTheYears"),
                publicationYearsToInclude
              )}
          </div>

          <div className={s.buttonFade}>
            <Button
              size="large"
              color="red"
              className={s.applySearchButton}
              onClick={() => this.performSearchAndToggleOverlay()}
            >
              {tx("misc.search")}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
